import React, { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Stack,
  Button,
  Flex,
  useDisclosure,
  Center,
  Badge
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon, SettingsIcon, AddIcon } from '@chakra-ui/icons';
import {
  useGroupingDetails,
  useApproveGrouping,
  useRejectGrouping,
  useProcessGrouping,
  useAddListingsToGrouping,
  useRemoveListingsFromGrouping
} from 'hooks/queries/useGroupings';
import Loader from 'components/Loader/Loader';
import ItemCard from 'components/Item/ItemCard/ItemCard';
import ListingGrid from 'components/Listing/ListingGrid/ListingGrid';
import { Modal, GroupingCreationWizard } from 'components';
import { NavigationService } from 'services';
import { ItemModel, ListingModel } from 'models';

export default function GroupingDetailsView() {
  const { groupingId } = useParams();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleItemClick = (item: ItemModel) => {
    navigate(NavigationService.getItemDetailsPath(item.id, item.getDisplayName()));
  };

  const handleListingClick = (listing: ListingModel) => {
    navigate(NavigationService.getListingDetailsPath(listing.id, listing.getDisplayName()));
  };
  
  // React Query mutations
  const approveMutation = useApproveGrouping();
  const rejectMutation = useRejectGrouping();
  const processMutation = useProcessGrouping();
  const addListingsMutation = useAddListingsToGrouping();
  const removeListingMutation = useRemoveListingsFromGrouping();
  
  const { data: grouping, isLoading, isError } = useGroupingDetails(groupingId);

  // Handlers with actual mutation logic
  const handleApprove = () => {
    if (!groupingId) return;
    
    approveMutation.mutate(
      { grouping_id: groupingId }
    );
  };

  const handleReject = () => {
    if (!groupingId) return;
    
    rejectMutation.mutate(
      { grouping_id: groupingId }
    );
  };

  const handleProcess = () => {
    if (!groupingId) return;
    
    processMutation.mutate(
      { grouping_id: groupingId }
    );
  };

  const handleAddListings = useCallback((item: ItemModel, listings: ListingModel[]) => {
    if (!groupingId || listings.length === 0) return;

    addListingsMutation.mutate(
      {
        id: groupingId,
        payload: { listings: listings.map(l => l.id) }
      },
      {
        onSuccess: () => {
          onClose();
        }
      }
    );
  }, [groupingId, addListingsMutation, onClose]);

  const handleRemoveListing = (listingId: string) => {
    if (!groupingId) return;
    
    removeListingMutation.mutate(
      { 
        id: groupingId, 
        payload: { listings: [listingId] } 
      }
    );
  };

  if (isLoading) return <Loader />;
  if (isError || !grouping) return <Center p={8}><Text>Failed to load grouping details.</Text></Center>;

  return (
    <Box maxW="1200px" mt="100px" p={4}>
      <Stack spacing={6}>
        {/* Item and Grouping Overview */}
        <Flex gap={6} direction={{ base: 'column', md: 'row' }}>
          <Box flex="1">
            <ItemCard item={grouping.item} size="md" onClick={() => handleItemClick(grouping.item)} clickOnImage clickOnName />
          </Box>
          <Box flex="2">
            <Card>
              <CardHeader bg="rose.500" color="white">
                <Heading size="md">Grouping Overview</Heading>
              </CardHeader>
              <CardBody>
                <Stack spacing={2}>
                  <Text><b>Name:</b> {grouping.name}</Text>
                  <Text><b>Status:</b> <Badge colorScheme={grouping.status === 'Approved' ? 'green' : grouping.status === 'Rejected' ? 'red' : 'blue'}>{grouping.status}</Badge></Text>
                  <Text><b>Number of Listings:</b> {grouping.listings.length}</Text>
                </Stack>
              </CardBody>
            </Card>
            {/* Actions */}
            <Card mt={4}>
              <CardHeader bg="gray.100">
                <Heading size="sm">Grouping Actions</Heading>
              </CardHeader>
              <CardBody>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                  <Button 
                    leftIcon={<AddIcon />} 
                    colorScheme="blue" 
                    onClick={onOpen}
                    isLoading={addListingsMutation.isPending}
                  >
                    Add Listings
                  </Button>
                  <Button 
                    leftIcon={<CheckIcon />} 
                    colorScheme="green" 
                    onClick={handleApprove}
                    isLoading={approveMutation.isPending}
                    isDisabled={grouping.status === 'Approved'}
                  >
                    Approve
                  </Button>
                  <Button 
                    leftIcon={<CloseIcon />} 
                    colorScheme="red" 
                    onClick={handleReject}
                    isLoading={rejectMutation.isPending}
                    isDisabled={grouping.status === 'Rejected'}
                  >
                    Reject
                  </Button>
                  <Button 
                    leftIcon={<SettingsIcon />} 
                    colorScheme="purple" 
                    onClick={handleProcess}
                    isLoading={processMutation.isPending}
                    isDisabled={grouping.status !== 'Approved'}
                  >
                    Process
                  </Button>
                </Stack>
              </CardBody>
            </Card>
          </Box>
        </Flex>
        {/* Listings Grid */}
        <Card>
          <CardHeader bg="gray.100">
            <Heading size="sm">Listings ({grouping.listings.length})</Heading>
          </CardHeader>
          <CardBody>
            {grouping.listings.length === 0 ? (
              <Text>No listings in this grouping.</Text>
            ) : (
              <ListingGrid
                gridListings={grouping.listings}
                onListingClick={handleListingClick}
                onRemoveListingClick={handleRemoveListing}
                isLoading={isLoading || removeListingMutation.isPending}
              />
            )}
          </CardBody>
        </Card>
      </Stack>
      {/* Add Listings Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Add Listings to Grouping"
        content={
          <GroupingCreationWizard
            defaultArtistId={grouping?.item?.artistId}
            defaultItem={grouping?.item}
            initialWorkflow="item-first"
            onSubmit={handleAddListings}
            isLoading={addListingsMutation.isPending}
          />
        }
        showCloseButton={false}
        showSubmitButton={false}
        size="4xl"
      />
    </Box>
  );
} 
